/**
 * Auth Store med Zustand
 * Konverterat från deque-web-main Redux slice
 */

import { create } from 'zustand';
import Cookies from 'js-cookie';
import type { AuthState, SignupResponseType, LoginResponseType, LoginDetailsPayload, ImpersonationState } from '@/types/auth';
import { useLanguageStore } from './languageStore';
import { uiLanguage } from '@/utils/uiLocale';
import { usePermissionsStore } from './permissionsStore';
import { clearAllFleetOverviewSessionState } from '@/utils/fleetOverviewSession';

interface AuthStore extends AuthState {
  // Actions
  setSignUpUser: (data: SignupResponseType) => void;
  clearUser: () => void;
  setLoginUserData: (data: LoginResponseType) => void;
  setLoginDetails: (data: LoginDetailsPayload) => void;
  logOutUser: () => void;
  // Impersonation actions
  startImpersonation: (data: LoginDetailsPayload, impersonation: ImpersonationState) => void;
  stopImpersonation: (data: LoginDetailsPayload) => void;
}

// Ladda användardata från localStorage om den finns
const loadUserFromStorage = (): AuthState['user'] => {
  try {
    const userStr = localStorage.getItem('user');
    if (userStr) {
      return JSON.parse(userStr);
    }
  } catch (error) {
    console.error('Error loading user from localStorage:', error);
  }
  return null;
};

// Ladda impersonation state från localStorage
const loadImpersonationFromStorage = (): ImpersonationState | null => {
  try {
    const impStr = localStorage.getItem('impersonation');
    if (impStr) {
      return JSON.parse(impStr);
    }
  } catch (error) {
    console.error('Error loading impersonation from localStorage:', error);
  }
  return null;
};

const sessionMarker = (): string | null => (Cookies.get('dqSession') === '1' ? 'cookie' : null);

const initialState: AuthState = {
  signUpUserData: null,
  loginUserData: null,
  accessToken: sessionMarker() || Cookies.get('accessToken') || null,
  refreshToken: sessionMarker() || Cookies.get('refreshToken') || null,
  user: loadUserFromStorage(),
  impersonation: loadImpersonationFromStorage(),
};

export const useAuthStore = create<AuthStore>((set, get) => ({
  ...initialState,

  /**
   * Spara signup user data (används mellan signup → OTP steg)
   */
  setSignUpUser: (data: SignupResponseType) => {
    set({ signUpUserData: data });
  },

  /**
   * Rensa signup user data
   */
  clearUser: () => {
    set({ signUpUserData: null });
  },

  /**
   * Spara login user data (används mellan login → OTP steg)
   */
  setLoginUserData: (data: LoginResponseType) => {
    set({ loginUserData: data });
  },

  /**
   * Spara login details efter lyckad autentisering
   * Sparar tokens i cookies och state
   */
  setLoginDetails: (data: LoginDetailsPayload) => {
    clearAllFleetOverviewSessionState();
    // Rensa ev. behörigheter från en tidigare användare så att den nya
    // användaren alltid hämtar sina egna permissions (undviker stale cache
    // vid byte av användare, t.ex. logga ut + logga in inom TTL-fönstret)
    usePermissionsStore.getState().clearPermissions();
    // Token sätts av servern som httpOnly-cookies. Rensa gamla js-läsbara kopior.
    Cookies.remove('accessToken');
    Cookies.remove('refreshToken');

    // Spara användardata i localStorage
    try {
      localStorage.setItem('user', JSON.stringify(data.user));
    } catch (error) {
      console.error('Error saving user to localStorage:', error);
    }

    // Uppdatera state
    set({
      accessToken: 'cookie',
      refreshToken: 'cookie',
      user: data.user,
    });

    // Uppdatera språkval - prioritera databasen när användaren loggar in
    // Databasens preferred_language är den auktoritativa källan för inloggade användare
    const langToUse = uiLanguage(data.user.preferred_language);
    
    // Uppdatera localStorage FÖRST så att i18n kan läsa det korrekt
    localStorage.setItem('i18nextLng', langToUse);
    
    // Sätt språket direkt - använd direkt import för att säkerställa att det händer synkront
    useLanguageStore.getState().initializeLanguage(langToUse);
  },

  /**
   * Logga ut användare
   * Om användaren kommer från SAML-organisation, initiera SLO
   * Annars rensa cookies och state direkt
   */
  logOutUser: () => {
    clearAllFleetOverviewSessionState();
    // Rensa behörigheter direkt vid utloggning så att nästa användare inte
    // ärver förra användarens permissions (gäller även SAML/OIDC-redirect nedan)
    usePermissionsStore.getState().clearPermissions();
    const currentUser = get().user;
    const currentOrg = currentUser?.organization_id;

    // Kolla om användaren kommer från SAML-organisation
    if (currentUser?.auth_method === 'SAML2' && currentOrg) {
      // Initiera SLO - redirect till backend som redirectar till IdP
      window.location.href = `/api/auth/saml/${currentOrg}/logout`;
      return;
    }

    if (!(currentUser?.auth_method === 'SAML2' && currentOrg) && !(currentUser?.auth_method === 'OIDC' && currentOrg)) {
      void fetch('/api/auth/logout', { method: 'POST', credentials: 'include' });
    }

    // OIDC-organisation: POST till backend som redirectar till IdP logout
    if (currentUser?.auth_method === 'OIDC' && currentOrg) {
      const form = document.createElement('form');
      form.method = 'POST';
      form.action = `${window.location.origin}/api/auth/oidc/${currentOrg}/logout`;
      document.body.appendChild(form);
      form.submit();
      return;
    }

    // Lokal logout för PASSWORD-användare
    // Rensa cookies
    Cookies.remove('accessToken');
    Cookies.remove('refreshToken');
    Cookies.remove('dqSession');

    // Rensa localStorage
    try {
      localStorage.removeItem('user');
      localStorage.removeItem('impersonation');
    } catch (error) {
      console.error('Error removing user from localStorage:', error);
    }

    // Rensa state
    set({
      accessToken: null,
      refreshToken: null,
      user: null,
      loginUserData: null,
      signUpUserData: null,
      impersonation: null,
    });
  },

  /**
   * Starta impersonation - Växla till en annan användare
   */
  startImpersonation: (data: LoginDetailsPayload, impersonation: ImpersonationState) => {
    clearAllFleetOverviewSessionState();
    Cookies.remove('accessToken');
    Cookies.remove('refreshToken');

    // Spara användardata och impersonation i localStorage
    try {
      localStorage.setItem('user', JSON.stringify(data.user));
      localStorage.setItem('impersonation', JSON.stringify(impersonation));
    } catch (error) {
      console.error('Error saving impersonation to localStorage:', error);
    }

    // Rensa permissions så de re-fetchar för den nya användaren
    usePermissionsStore.getState().clearPermissions();

    // Uppdatera state
    set({
      accessToken: 'cookie',
      refreshToken: 'cookie',
      user: data.user,
      impersonation: impersonation,
    });
  },

  /**
   * Avsluta impersonation - Gå tillbaka till admin-kontot
   */
  stopImpersonation: (data: LoginDetailsPayload) => {
    clearAllFleetOverviewSessionState();
    Cookies.remove('accessToken');
    Cookies.remove('refreshToken');

    // Uppdatera localStorage
    try {
      localStorage.setItem('user', JSON.stringify(data.user));
      localStorage.removeItem('impersonation');
    } catch (error) {
      console.error('Error clearing impersonation from localStorage:', error);
    }

    // Rensa permissions så de re-fetchar för den ursprungliga användaren
    usePermissionsStore.getState().clearPermissions();

    // Uppdatera state
    set({
      accessToken: 'cookie',
      refreshToken: 'cookie',
      user: data.user,
      impersonation: null,
    });
  },
}));

// Selectors för enklare användning
export const useAuth = () => {
  const accessToken = useAuthStore((state) => state.accessToken);
  const refreshToken = useAuthStore((state) => state.refreshToken);
  const user = useAuthStore((state) => state.user);
  const impersonation = useAuthStore((state) => state.impersonation);
  const isAuthenticated = !!accessToken;

  return {
    accessToken,
    refreshToken,
    user,
    impersonation,
    isAuthenticated,
    isImpersonating: impersonation?.isImpersonating || false,
  };
};

export const useAuthActions = () => {
  const setSignUpUser = useAuthStore((state) => state.setSignUpUser);
  const clearUser = useAuthStore((state) => state.clearUser);
  const setLoginUserData = useAuthStore((state) => state.setLoginUserData);
  const setLoginDetails = useAuthStore((state) => state.setLoginDetails);
  const logOutUser = useAuthStore((state) => state.logOutUser);
  const startImpersonation = useAuthStore((state) => state.startImpersonation);
  const stopImpersonation = useAuthStore((state) => state.stopImpersonation);

  return {
    setSignUpUser,
    clearUser,
    setLoginUserData,
    setLoginDetails,
    logOutUser,
    startImpersonation,
    stopImpersonation,
  };
};

